import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {makeReactContext} from 'lib/makeReactContext';
import {SectionConfiguration} from 'notebook/ELNConstants';
import {Note} from 'notebook/util/NoteUtilConstants';

export const getDefaultSectionConfigurationContext = (): SectionConfiguration => ({
  allows: {
    collapse: BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS,
    date: true,
    insertion: true,
    preamble: false,
    title: BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS,
  },
});

export const SectionConfigurationContext = makeReactContext<SectionConfiguration>(
  getDefaultSectionConfigurationContext,
  'SectionConfigurationContext'
);

/**
 * Simple setup for using context to hydrate the NoteEditor subtree with useful state.
 *
 * Two separate context objects are created here:
 *  - EditorPropertiesContext, which tracks some important metadata, and
 *  - NoteContext, which tracks the full note object
 *
 * The context values are separated to allow faster `isEqual` checks. See `makeReactContext`
 * for details.
 */

export interface EditorPropertiesContextInterface {
  showFormulas: boolean;
  highlightCustomColumns?: boolean;
  isConfig?: boolean | null;
  rootFolderId?: string;
  templateCollectionId?: string;
  fileId?: string;
  isExportView?: boolean;
  isPrintPreviewView?: boolean;
}

function _isExportViewRequiresIsPrintPreview({
  isExportView,
  isPrintPreviewView,
}: EditorPropertiesContextInterface): void {
  if (isExportView) {
    assertAndContinue(
      Boolean(isPrintPreviewView),
      'PrintPreviewView should always be active for isExportView'
    );
  }
}

export const EditorPropertiesContext = makeReactContext<EditorPropertiesContextInterface>(
  /*
   isConfig is newly introduced for CUST-7764 and may not be set as expected by all Providers of this context,
   but is used by DocumentEditor to ensure that components within a procedure document can easily access
   whether they are part of a Document that represents configuration, not a particular execution
   TODO(BNCH-98830) set this context in all providers and remove this warning
   */
  () => ({
    showFormulas: false,
    highlightCustomColumns: false,
    isPrintPreviewView: false,
    isConfig: null,
  }),
  'EditorPropertiesContext',
  isEqual,
  {validationFn: _isExportViewRequiresIsPrintPreview}
);

/*
  WARNING: the following context value is
    - large
    - complex
    - ever-changing

  And the `makeReactContext` tool runs the value through a deep-equality checker every time the provider
  is rendered. So don't render the provider unnecessarily!

  If we run into a situation in which changes to the note (as a whole!) are required rapidly, or end up
  rendering too many NoteContext.Consumer components (this will wreck us!), we can reevaluate:
    * Break up the data which we broadcast to have fewer dependencies, as we did above in
      EditorPropertiesContext
    * Move the required data to the plugin's `getItemProps`, which avoids context issues altogether
    * Consider debouncing the equality check (which will be good for performance, but potentially make
      things hard to reason about)
*/

export interface NoteContextInterface {
  note?: Note;
}

export const NoteContext = makeReactContext<NoteContextInterface>(() => ({}), 'NoteContext');
